import React from 'react';
import { goToNextStep, goToPreviousStep } from '@src/stores/newFlowStore';
import { useReturnStore } from '@src/stores/returnStore';
import { ReturnHeaders } from '@src/components/shared/ReturnHeaders';
import { ReturnButtons } from '@src/components/shared/ReturnButtons';
import TechnicalErrorAlert from '@src/components/shared/TechnicalErrorAlert';
import QuantitySelectionItems from '@src/components/quantity-selection/QuantitySelectionItems';
import {
  checkForQtyReasonErrors,
  emptyQtyReasonErrors,
} from '@src/stores/errors/errorQtyReasonStore';
import {
  TagReturnQuantityReasonSubmission,
  TagOnReturnReasonQuantityErrorMessage,
} from '@src/utils/analytics-tagging';
import { getReturnMethodSelectionMode } from '@src/utils/returnFlow';
import { isROSEnabled } from '@src/constants/fs-values';
import { useReturnMethods } from '@src/hooks/useReturnMethods';
import { getReturnsFraudPrompts } from '@src/utils';
import FraudModal from '@src/components/shared/fraud/FraudModal';

const QuantitySelectionStep = () => {
  const selectedItems = useReturnStore((state) => state.selectedItems);
  const militaryExchange = useReturnStore((state) => state.militaryExchange);
  const { isStandardWholeOrderFlow } =
    getReturnMethodSelectionMode(selectedItems);
  const {
    fetchReturnMethods,
    isReturnMethodsLoading,
    hasReturnMethodsError,
    clearReturnMethodsError,
    fraudOutcome,
    clearFraudOutcome,
  } = useReturnMethods();

  // ROS: the methods answer must exist before the method screen renders, so it
  // is fetched here and the step only advances once it lands. Kept out of the
  // click handler itself so that handler stays synchronous, matching the
  // method step's Continue.
  //
  // The fraud check now answers here too, before the customer has seen a single
  // return method. Any outcome holds the step: a warning advances only when the
  // customer accepts it, and the other two do not advance at all.
  const continueWithReturnMethods = async () => {
    const adaptedMethods = await fetchReturnMethods();
    if (!adaptedMethods) return;
    goToNextStep();
  };

  const acceptWarningAndContinue = () => {
    clearFraudOutcome();
    goToNextStep();
  };

  const fraudPrompt =
    fraudOutcome &&
    getReturnsFraudPrompts(
      fraudOutcome.message,
      clearFraudOutcome,
      acceptWarningAndContinue,
    )[fraudOutcome.type];

  const proceedToNextStep = () => {
    const hasErrors = checkForQtyReasonErrors(selectedItems);
    if (hasErrors) {
      TagOnReturnReasonQuantityErrorMessage();
      return;
    }
    if (!isStandardWholeOrderFlow) {
      TagReturnQuantityReasonSubmission(selectedItems, militaryExchange);
    }
    if (isROSEnabled()) {
      continueWithReturnMethods();
      return;
    }
    goToNextStep();
  };

  const proceedBack = () => {
    emptyQtyReasonErrors();
    // Belt and braces rather than the mechanism: navigating away unmounts this
    // step and takes the state with it. Kept because the step surviving a
    // navigation is a rendering decision made elsewhere, and this should not
    // start leaking a stale error the day that changes.
    clearReturnMethodsError();
    clearFraudOutcome();
    goToPreviousStep();
  };

  return (
    <div>
      <ReturnHeaders title="Select return quantity and reason" />
      {hasReturnMethodsError && <TechnicalErrorAlert />}
      <QuantitySelectionItems disabled={isReturnMethodsLoading} />
      <ReturnButtons
        proceedNext={proceedToNextStep}
        proceedBack={proceedBack}
        loading={isReturnMethodsLoading}
      />
      {fraudPrompt && (
        <FraudModal
          returnFraudType={fraudOutcome?.type}
          title={fraudPrompt.title}
          body={fraudPrompt.bodyMessage}
          buttonText={fraudPrompt.buttonMessage}
          onClick={fraudPrompt.onClick}
        />
      )}
    </div>
  );
};

export default QuantitySelectionStep;
